<script lang="ts">
    import type {
        SingleTranslationCarrier,
        TranslationAddedCarrier,
    } from "@app/models/api";
    import { sendCurrentDictionaryAnalyticsEvent } from "@app/services/analytics/analytics-send-event";
    import { dictionary } from "@app/services/currentDictionary.service";
    import { FetchError } from "@app/services/FetchError";
    import { addImage } from "@app/services/glosbe-dictionary-api/add-image";
    import { addTranslation } from "@app/services/glosbe-dictionary-api/add-translation";
    import {
        CHANGES__TRANSLATION_ADD_PAIR,
        FORM__Add,
        FORM__Add_and_reset_form,
        FORM__batchForm,
        FORM__Cancel,
        FORM__definition__hint,
        FORM__definition__title,
        FORM__example__hint,
        FORM__example__title,
        FORM__gender__title,
        FORM__image__hint,
        FORM__image__title,
        FORM__logInToAddEntries,
        FORM__note__label,
        FORM__note__title,
        FORM__partOfSpeech__label,
        FORM__partOfSpeech__title,
        FORM__phrase__title,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { isLoggedIn } from "@app/services/transferState-service";
    import { slide } from "svelte/transition";
    import InlineMessage from "../InlineMessage.svelte";
    import LogInFirst from "../LogInFirst.svelte";
    import TextareaAutogrow from "../TextareaAutogrow.svelte";
    import AddTranslationFormFieldsGroup from "./AddTranslationFormFieldsGroup.svelte";
    import AddTranslationFormPhraseHints from "./AddTranslationFormPhraseHints.svelte";
    import AddTranslationHeader from "./AddTranslationHeader.svelte";

    export let onClose: (event: Event, id: string) => void;
    export let open: boolean = false;
    export let translatedPhrase: string;
    export let translationPhrase: string | undefined;
    export let id: string;

    const translationCarrier: SingleTranslationCarrier = {
        l1: $dictionary.l1.code,
        l2: $dictionary.l2.code,
        p1: translatedPhrase,
        p2: translationPhrase || "",
        left: {
            definition: "",
            example: "",
            note: "",
            gender: "",
            partOfSpeech: "",
        },
        right: {
            definition: "",
            example: "",
            note: "",
            gender: "",
            partOfSpeech: "",
        },
    };

    let image: FileList;
    let imageToUse: null | { file: File; objectUrl: string } = null;

    type FORM_STATE = "ready" | "waiting" | "error" | "success";
    let formState: FORM_STATE = "ready";
    let errorMessage: string | undefined;
    let translationAddedCarrier: TranslationAddedCarrier | undefined;

    async function onSubmit(event: Event) {
        event.preventDefault();
        const submitter: HTMLButtonElement = (event as any)
            .submitter as HTMLButtonElement;
        let shouldNavigateToNewWord = true;
        if (submitter && submitter.dataset["buttontype"] === "add-and-reset") {
            shouldNavigateToNewWord = false;
        }
        try {
            formState = "waiting";
            if (imageToUse) {
                const file = imageToUse.file;
                const imageCarrier = await addImage(file, false, "");
                translationCarrier.imageCarrier = imageCarrier;
            }
            translationAddedCarrier = await addTranslation(translationCarrier);
            if (translationAddedCarrier.errorMessage) {
                formState = "error";
                errorMessage = translationAddedCarrier.errorMessage;
            } else {
                formState = "success";
                translationCarrier.p2 = "";
                translationCarrier.right = {
                    definition: "",
                    example: "",
                    note: "",
                    gender: "",
                    partOfSpeech: "",
                };
                imageToUse = null;
                sendCurrentDictionaryAnalyticsEvent({
                    action: "add-translation",
                    label: translationCarrier.source,
                });
                if (shouldNavigateToNewWord) {
                    const url = `/${encodeURIComponent(
                        translationCarrier.l1,
                    )}/${encodeURIComponent(
                        translationCarrier.l2,
                    )}/${encodeURIComponent(
                        translationCarrier.p1 || "",
                    )}#translation-${translationAddedCarrier.translationId}`;

                    let shouldReload = false;
                    if (url.startsWith(window.location.pathname)) {
                        shouldReload = true;
                    }
                    window.location.href = url;

                    if (shouldReload) {
                        window.location.reload();
                    }
                }
            }
        } catch (e: any) {
            errorMessage = "" + e;
            formState = "error";
            if (e instanceof FetchError) {
                const fetchError = e as FetchError;
                const errorCodes: string[] | undefined =
                    fetchError.json?.errorCodes;
                if (errorCodes) {
                    if (
                        errorCodes.indexOf(
                            "org.springframework.security.access.AccessDeniedException",
                        ) >= 0
                    ) {
                        errorMessage = getMessage(FORM__logInToAddEntries);
                    } else if (fetchError.json?.message) {
                        errorMessage = fetchError.json.message;
                    }
                }
            }
        }
    }

    async function removeImage() {
        if (image) {
            imageToUse = null;
        }
    }

    async function onImageChange() {
        setTimeout(() => {
            if (imageToUse) {
                try {
                    URL.revokeObjectURL(imageToUse.objectUrl);
                } catch (e) {}
            }
            if (image && image[0]) {
                imageToUse = {
                    file: image[0],
                    objectUrl: URL.createObjectURL(image[0]),
                };
            }
        });
    }

    let formElement: HTMLFormElement;
    let submitButton: HTMLButtonElement;

    function onHeaderSaveClick(event: Event) {
        event.preventDefault();
        event.stopPropagation();
        if (formState !== "waiting") {
            submitButton.click();
        }
    }

    function p1ToLowercase() {
        if (translationCarrier.p1 !== undefined) {
            const newTranslationCarrier = { ...translationCarrier };
            newTranslationCarrier.p1 = translationCarrier.p1.toLowerCase();
            translationCarrier.p1 = translationCarrier.p1.toLowerCase();
        }
    }

    function onCloseInternal(event: Event) {
        onClose(event, id);
    }
</script>

{#if open}
    <div
        transition:slide
        class="border-t border border-gray-300 my-2 shadow-lg rounded"
    >
        {#if isLoggedIn() && translationCarrier.left && translationCarrier.right}
            <AddTranslationHeader
                onClose={onCloseInternal}
                onSave={onHeaderSaveClick}
            />
            {#if errorMessage}
                <div class="p-2">
                    <InlineMessage
                        messageType="error"
                        messageText={errorMessage}
                    />
                </div>
            {/if}
            {#if formState == "success"}
                <div class="p-2">
                    <InlineMessage
                        messageType="info"
                        message={CHANGES__TRANSLATION_ADD_PAIR}
                    />
                    <div class="text-right mt-2">
                        <button
                            type="button"
                            class="px-2 py-1 w-8 h-6 text-gray-500"
                            on:click={onCloseInternal}
                            ><span class="icon-close" /></button
                        >
                    </div>
                </div>
            {/if}
            <form
                on:submit={onSubmit}
                class="text-sm text-gray-700 py-2"
                bind:this={formElement}
            >
                <div class="">
                    <div
                        class="flex flex-1 text-center text-sm text-gray-900 font-medium"
                    >
                        <div class="w-1/5"></div>
                        <div class="w-1/2">{$dictionary.l1.name}</div>
                        <div class="w-1/2">{$dictionary.l2.name}</div>
                    </div>
                </div>
                <div class="mb-2 px-1 py-1">
                    <div class="mx-1 border-b border-gray-300 border-dashed" />
                    <AddTranslationFormFieldsGroup
                        fieldNameMessage={FORM__phrase__title}
                        fieldDescriptionPlaceholderMessage={FORM__phrase__title}
                    >
                        <div slot="l1" let:placeholderL1 let:placeholderL2>
                            <TextareaAutogrow
                                clasz="p-1 border border-primary-400 bg-white w-full rounded"
                                required
                                maxlength={191}
                                spellcheck={false}
                                autocorrect="off"
                                autocomplete="off"
                                autocapitalize="none"
                                placeholder={placeholderL1}
                                dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                                bind:value={translationCarrier.p1}
                            />
                            <AddTranslationFormPhraseHints
                                inputPhrase={translationCarrier.p1 ?? ""}
                                inputPhraseToLowercase={p1ToLowercase}
                            />
                        </div>
                        <TextareaAutogrow
                            let:placeholderL1
                            let:placeholderL2
                            slot="l2"
                            clasz="p-1 border border-primary-400 bg-white w-full rounded"
                            required
                            maxlength={191}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL2}
                            dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.p2}
                        />
                    </AddTranslationFormFieldsGroup>

                    <AddTranslationFormFieldsGroup
                        fieldNameMessage={FORM__definition__title}
                        fieldDescriptionPlaceholderMessage={FORM__definition__title}
                        fieldHintMessage={FORM__definition__hint}
                    >
                        <TextareaAutogrow
                            slot="l1"
                            let:placeholderL1
                            let:placeholderL2
                            clasz="p-1 border border-primary-400 bg-white w-full rounded"
                            maxlength={2000}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL1}
                            dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.left.definition}
                        />
                        <TextareaAutogrow
                            slot="l2"
                            let:placeholderL1
                            let:placeholderL2
                            clasz="p-1 border border-primary-400 bg-white w-full rounded"
                            maxlength={2000}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL2}
                            dir={$dictionary.l2.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.right.definition}
                        />
                    </AddTranslationFormFieldsGroup>

                    <AddTranslationFormFieldsGroup
                        fieldNameMessage={FORM__example__title}
                        fieldDescriptionPlaceholderMessage={FORM__example__title}
                        fieldHintMessage={FORM__example__hint}
                    >
                        <TextareaAutogrow
                            slot="l1"
                            let:placeholderL2
                            let:placeholderL1
                            clasz="p-1 border border-primary-400 bg-white w-full rounded"
                            maxlength={1000}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL1}
                            dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.left.example}
                        />
                        <TextareaAutogrow
                            slot="l2"
                            let:placeholderL2
                            let:placeholderL1
                            clasz="p-1 border border-primary-400 bg-white w-full rounded"
                            maxlength={1000}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL2}
                            dir={$dictionary.l2.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.right.example}
                        />
                    </AddTranslationFormFieldsGroup>

                    <AddTranslationFormFieldsGroup
                        fieldNameMessage={FORM__gender__title}
                        fieldDescriptionPlaceholderMessage={FORM__gender__title}
                    >
                        <input
                            slot="l1"
                            let:placeholderL2
                            let:placeholderL1
                            class="p-1 border border-primary-400 bg-white w-full rounded"
                            type="text"
                            maxlength={50}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL1}
                            dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.left.gender}
                        />

                        <input
                            slot="l2"
                            let:placeholderL2
                            let:placeholderL1
                            class="p-1 border border-primary-400 bg-white w-full rounded"
                            type="text"
                            maxlength={50}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL2}
                            dir={$dictionary.l2.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.right.gender}
                        />
                    </AddTranslationFormFieldsGroup>

                    <AddTranslationFormFieldsGroup
                        fieldNameMessage={FORM__partOfSpeech__title}
                        fieldDescriptionPlaceholderMessage={FORM__partOfSpeech__label}
                    >
                        <input
                            slot="l1"
                            let:placeholderL2
                            let:placeholderL1
                            class="p-1 border border-primary-400 bg-white w-full rounded"
                            type="text"
                            maxlength={50}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL1}
                            dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.left.partOfSpeech}
                        />
                        <input
                            slot="l2"
                            let:placeholderL2
                            let:placeholderL1
                            class="p-1 border border-primary-400 bg-white w-full rounded"
                            type="text"
                            maxlength={50}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL2}
                            dir={$dictionary.l2.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.right.partOfSpeech}
                        />
                    </AddTranslationFormFieldsGroup>
                    <AddTranslationFormFieldsGroup
                        fieldNameMessage={FORM__note__title}
                        fieldDescriptionPlaceholderMessage={FORM__note__label}
                    >
                        <input
                            slot="l1"
                            let:placeholderL2
                            let:placeholderL1
                            class="p-1 border border-primary-400 bg-white w-full rounded"
                            type="text"
                            maxlength={2000}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL1}
                            dir={$dictionary.l1.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.left.note}
                        />
                        <input
                            slot="l2"
                            let:placeholderL2
                            let:placeholderL1
                            class="p-1 border border-primary-400 bg-white w-full rounded"
                            type="text"
                            maxlength={2000}
                            spellcheck={false}
                            autocorrect="off"
                            autocomplete="off"
                            autocapitalize="none"
                            placeholder={placeholderL2}
                            dir={$dictionary.l2.rtl ? "rtl" : "ltr"}
                            bind:value={translationCarrier.right.note}
                        />
                        <span slot="hint">
                            'F.ex.: vulgar / common / archaic / dialect'
                        </span>
                    </AddTranslationFormFieldsGroup>
                    <div
                        class="flex px-1 odd:bg-slate-50 border-b border-gray-300 border-dashed py-3"
                    >
                        <h4
                            class="text-sm font-medium text-gray-900 items-center flex w-1/5"
                        >
                            {getMessage(FORM__image__title)}
                        </h4>

                        <div
                            class="flex items-center justify-between p-2 flex-1"
                        >
                            <label class="button-sm">
                                {getMessage(FORM__image__hint)}
                                <input
                                    class="p-1 border border-primary-400 bg-white w-full rounded hidden"
                                    type="file"
                                    accept="image/*"
                                    bind:files={image}
                                    on:change={onImageChange}
                                />
                            </label>
                            {#if imageToUse}
                                <img
                                    src={imageToUse.objectUrl}
                                    class="w-24 h-24"
                                    alt="uploaded"
                                />
                                <button
                                    type="button"
                                    class="button-xs"
                                    on:click={removeImage}
                                    ><span
                                        class="icon-delete-forever-outline"
                                    /></button
                                >
                            {/if}
                        </div>
                    </div>
                </div>

                {#if errorMessage}
                    <div class="p-2">
                        <InlineMessage
                            messageType="error"
                            messageText={errorMessage}
                        />
                    </div>
                {/if}
                <div class="flex">
                    <div class="p-2 text-sm flex-1 items-center flex">
                        <a
                            href={"https://app.glosbe.com/add-multiple?l1=" +
                                $dictionary.l1.code +
                                "&l2=" +
                                $dictionary.l2.code}
                            >{getMessage(FORM__batchForm)}</a
                        >
                    </div>
                    <div class="text-right p-2">
                        <button
                            type="button"
                            class="button-sm"
                            on:click={onCloseInternal}
                            >{getMessage(FORM__Cancel)}</button
                        >
                        <button
                            type="submit"
                            data-buttontype="add-and-reset"
                            disabled={formState !== "ready"}
                            bind:this={submitButton}
                            class="button-sm"
                            >{getMessage(FORM__Add_and_reset_form)}</button
                        >
                        <button
                            type="submit"
                            disabled={formState !== "ready"}
                            bind:this={submitButton}
                            class="button-sm button-primary"
                            >{getMessage(FORM__Add)}</button
                        >
                    </div>
                </div>
            </form>
        {:else}
            <LogInFirst />
        {/if}
    </div>
{/if}
